/**
 * This is only used for the renewal settings for premium users now. It's replaced with
 * AccountPanelView2 for users who haven't yet upgraded.
 */

import * as preact from "preact";

import * as localAPI from "localAPI";
import * as modalController from "modalController";
import readlangClient from "readlangClient";
import * as subscriptionPlans from "shared/subscriptionPlans";
import * as signupModal from "signupModal";
import stores from "stores/stores";
import * as upsellTimer from "upsellTimer";
import AccountRenewalView from "views/AccountRenewalView";

const isBookmarklet = window.location.pathname === "/bookmarklet";

interface AccountPanelProps {
  accountPage?: boolean;
  allPhrasesLimit?: boolean;
  heading?: string;
  phraseLimit?: boolean;
  showTitle?: boolean;
  smartDefinitions?: boolean;
  socialButtons?: boolean;
}

interface AccountPanelState {
  claimingExtraPhrases: boolean;
  localMode?: boolean;
}

export class AccountPanel extends preact.Component<
  AccountPanelProps,
  AccountPanelState
> {
  state: AccountPanelState = {
    claimingExtraPhrases: false,
  };

  componentDidMount() {
    upsellTimer.reset();
    localAPI.isLocalMode().then((localMode) => {
      this.setState({ localMode });
    });
  }

  goToCheckout = (event: MouseEvent) => {
    if (isBookmarklet) {
      event.preventDefault();
      event.stopPropagation();

      const protocol =
        window.location.hostname === "localhost" ? "http://" : "https://";
      window.top.location.href = protocol + window.location.host + "/checkout";
    }
  };

  loginLink = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    $("#loginButton").click();
  };

  signupLink = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    signupModal.show({
      stores,
    });
  };

  extraPhrases = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ claimingExtraPhrases: true });
    readlangClient.request("POST", "/api/user/claimExtraPhrases", {}, () => {
      const acheivements = [...(stores.user.state.value.acheivements || [])];
      if (acheivements.indexOf("extraPhrases") === -1) {
        acheivements.push("extraPhrases");
      }
      stores.user.patch({ acheivements });
      modalController.cancel();
    });
  };

  continue = () => {
    modalController.cancel();
  };

  render() {
    if (this.state.localMode === undefined) {
      return null;
    }

    const user = stores.user.state.value;
    const plan = user.subscriptionPlan;
    const free = !["freeForever", "standard", "plus"].includes(plan ?? "");
    const freeForever = plan === "freeForever";
    const standard = plan === "standard";
    const localMode = this.state.localMode;
    const showTitle = this.props.showTitle ?? true;
    const showExtraPhrases =
      this.props.phraseLimit &&
      !(user.acheivements || []).includes("extraPhrases");
    const phraseLimit =
      user.phraseLimit || subscriptionPlans.permissions.free.phrases;

    return (
      <>
        {showTitle ? (
          <h2 class="spacing" style={{ textAlign: "center" }}>
            {free ? "Go Premium" : "Your Premium Account"}
          </h2>
        ) : null}

        <div class="planContainer">
          <div class="plan">
            <h2>Free</h2>
            <ul>
              <li>
                Unlimited <small>single word translations</small>
              </li>
              <li>
                10 <small>phrase translations / day</small>
              </li>
              <li>
                6 <small>words / phrase</small>
              </li>
              {this.props.smartDefinitions ? (
                <li>
                  10 <small>context explanations / day</small>
                </li>
              ) : null}
              <li>
                0 <small>streak repairs</small>
              </li>
            </ul>
            {!localMode && free ? (
              <div class="selected">Current Plan</div>
            ) : null}
          </div>
          <div class="plan suggested">
            <h2>
              <i class="icon-rocket"></i> &nbsp; Premium
            </h2>
            <ul>
              <li>
                Unlimited <small>single word translations</small>
              </li>
              <li>
                Unlimited <small>phrase translations</small>
              </li>
              <li>
                Context-Aware <small>translations</small>
              </li>
              <li>
                12 <small>words / phrase</small>
              </li>
              {this.props.smartDefinitions ? (
                <li>
                  Unlimited <small>context explanations</small>
                </li>
              ) : null}
              <li>Practice or export flashcards from specific books</li>
              <li>
                2 <small>streak repairs / month</small>
              </li>
            </ul>

            {localMode || freeForever ? (
              <>
                <h3>Pricing</h3>
                <ul>
                  <li>$6 / month</li>
                  <li>
                    $48 / year{" "}
                    <small>
                      <i>(only $4 / month)</i>
                    </small>
                  </li>
                </ul>
              </>
            ) : null}
            {localMode ? (
              <>
                <a class="signupLink" href="#" onClick={this.signupLink}>
                  Sign in
                </a>{" "}
                to try the full free version first.
              </>
            ) : null}

            {!localMode && !standard && !freeForever ? (
              <a
                class="goToCheckout prominent payButton"
                href="/checkout"
                onClick={this.goToCheckout}
              >
                <i class="icon-rocket icon-2x"></i> &nbsp; &nbsp;
                <span
                  style={{
                    display: "inline-block",
                    textAlign: "left",
                    verticalAlign: "middle",
                  }}
                >
                  Go Premium
                  <br />
                  from $4 per month
                  <div
                    style={{
                      paddingTop: 5,
                      fontWeight: "normal",
                      fontSize: 12,
                    }}
                  >
                    Click to Learn More
                  </div>
                </span>
              </a>
            ) : null}

            {!localMode && freeForever ? (
              <div class="selected">
                As thanks for being one of the first 600 beta testers, you have
                free Lifetime Membership!
              </div>
            ) : null}
          </div>
        </div>

        {standard ? <AccountRenewalView /> : null}

        {!freeForever && standard ? (
          <>
            <h2 style={{ textAlign: "center" }}>
              A Message From Readlang's Creator
            </h2>
            <p>Thanks for upgrading!</p>
            <p>
              Please don't hesitate to get in touch if you need any help or have
              any suggestions, either via the{" "}
              <a href="https://forum.readlang.com/" target="_blank">
                forum
              </a>{" "}
              or <a href="mailto:steve@readlang.com">email me directly</a>.
            </p>
            <p>Steve</p>
          </>
        ) : null}

        {freeForever ? (
          <>
            <p>
              Thanks for being one of the first 600 users of the Readlang beta.
              You took your time to try out an unproven site and many of you
              have helped by providing feedback and telling your friends. You're
              awesome :-)
            </p>
            <p>Steve</p>
          </>
        ) : null}

        <div style={{ clear: "both" }}>
          {this.props.phraseLimit ? (
            showExtraPhrases ? (
              this.state.claimingExtraPhrases ? (
                <img src="/content/throbber.gif" />
              ) : (
                <button
                  class="payButton extraPhrases"
                  onClick={this.extraPhrases}
                >
                  <i class="icon-gift icon-large"></i>
                  &nbsp; Just this once, claim {phraseLimit} more phrase
                  translations for free!
                </button>
              )
            ) : (
              <button
                class="lessProminent payButton continue"
                onClick={this.continue}
              >
                Continue for free
                <br class="mobileOnly" /> (single words only until tomorrow)
              </button>
            )
          ) : null}
          {this.props.allPhrasesLimit ? (
            <button
              class="payButton lessProminent continue"
              onClick={this.continue}
            >
              Continue for free
              <br class="mobileOnly" /> without highlights
            </button>
          ) : null}
        </div>
      </>
    );
  }
}
